import { AppContext } from "App";

import { IFormProfile } from "@multiweb/types/contracts";
import { Event } from "Global/Base/Events";
import { DeferredStateful, PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { ErrorHandler } from "UI/Errors";
import { FormGroupContainer } from "UI/UI/DefaultInterface/FormGroupContainer";
import { IFRMParams } from "../Controls/FormParameters";
import { FormUICustomProperty } from "../FormUI/FormUI";
import { IOpenedFormsHistoryManager } from "../Helpers/IOpenedFormsHistoryManager";
import { UIManager } from "../UIManager";
import { IWindowable, WindowState } from "../UIManagerInterfaces";
import { IForm } from "../XGControls/IForm";
import { FormGroup } from "./FormGroup";
import { Homepage } from "./Homepage";
import { IFormGroup, IFormGroupOpenModalParams } from "./IFormGroup";
import { IFavoriteChangedEvent, IFormManager, IOpenedForm } from "./IFormManager";
import { FormManagerProperty, ICustomTitleChangedInfo, IFavoriteFormChanged, IFavoriteFormItem, IFavoriteFormsProfile, IFormProfileChanged, IHideCloseButtonChanged } from "./IFormManagerInterfaces";

export class DefFormManager implements IFormManager {
    public readonly formHideCloseButtonChanged: Event<IFormManager, IHideCloseButtonChanged> = new Event();
    public readonly formClosed: Event<IFormManager, IOpenedForm> = new Event();
    public readonly formOpened: Event<IFormManager, IOpenedForm> = new Event();
    public readonly formSwitched: Event<IFormManager, IOpenedForm> = new Event();
    public readonly formFavoriteChanged: Event<IFormManager, IFavoriteChangedEvent> = new Event();
    public readonly formCustomTitleChanged: Event<IFormManager, ICustomTitleChangedInfo> = new Event;
    public readonly favoriteFormSet: Event<IFormManager, IFavoriteFormChanged> = new Event;
    public readonly formProfileChanged: Event<IFormManager, IFormProfileChanged> = new Event;
    public get initialized(): Promise<IFormManager> {
        return this._initialized.promise;
    }

    public get openingFormPromise(): Promise<IOpenedForm> | undefined {
        return this._openedFormDefer?.promise;
    }

    public get openedForms(): ReadonlyArray<IOpenedForm> {
        return Array.from(this._openedForms);
    }
    public get lastOpenedForm(): IOpenedForm {
        return this._openedForms.length == 0 ? undefined : this._openedForms[this._openedForms.length - 1];
    }
    public get lastActiveForm(): IOpenedForm {
        return this._activityFormList.length == 0 ? undefined : this.getOpenedForm(this._activityFormList[this._activityFormList.length - 1]);
    }
    public get openedFormsHistoryManager(): IOpenedFormsHistoryManager {
        return this._openedFormsHistoryManager;
    }
    public get activityFormHistory(): ReadonlyArray<string> {
        return this._activityFormList;
    }

    public get isInitialized(): boolean {
        return this._initialized.isSettled;
    }

    protected _initialized: DeferredStateful<IFormManager> = PromiseUtils.deferStateful<IFormManager>();
    protected _formsTarget: HTMLElement | undefined;

    protected readonly _openedFormIdsCounters: Map<number | string, number> = new Map();

    protected _openedFormsMap: Map<string, IOpenedForm> = new Map;
    protected _openedForms: IOpenedForm[] = [];
    protected _activityFormList: string[] = [];
    protected _favoriteForms: IFavoriteFormItem[] = [];
    protected readonly _openedFormsHistoryManager: IOpenedFormsHistoryManager;
    protected _openedFormDefer: DeferredStateful<IOpenedForm> | null = null;

    protected _homepage: Homepage | undefined;
    protected static _customFormNum: number = 0;

    public readonly previewId?: string;

    public setProperty(property: FormManagerProperty.favoriteForm, value: { menuId: number; formName: string; value: boolean }): Promise<void>;
    public setProperty(property: FormManagerProperty, value: any): Promise<void> {
        if (property == FormManagerProperty.favoriteForm) {
            this._toggleFavoriteForm(value.menuId, value.formName, value.value, value.profile);
        }

        return Promise.resolve();
    }
    public getProperty(property: FormManagerProperty, args?: any): any {
        if (property == FormManagerProperty.favoriteForm) {
            if (args == undefined) {
                return this._favoriteForms;
            } else {
                return this._isFavoriteForm(args.menuId, args.formCode, args.profileId);
            }
        }
        return Promise.resolve();
    }

    public getOpenedForm(formInstanceId: string): IOpenedForm {
        return this._openedFormsMap.get(formInstanceId);
    }

    /**
     *
     * @param formsPlaceElement Element kam budou umistovany formulare
     */
    constructor(formsPlaceElement: HTMLElement, openedFormsHistoryManager: IOpenedFormsHistoryManager, previewId: string) {
        this._openedFormsHistoryManager = openedFormsHistoryManager;
        this._formsTarget = formsPlaceElement;
        this.previewId = previewId;
        this.init();

        window.addEventListener("beforeunload", async (event) => {
            const isAnyCurrentFormEdited = await this.isAnyFormEdited();
            if (isAnyCurrentFormEdited) {
                event.preventDefault();
                event.returnValue = "";
            } else {
                return undefined;
            }
        });
    }
    public async isAnyFormEdited(): Promise<boolean> {
        const currentOpenedForms = [...this._openedForms, this._homepage];
        for (const openedForm of currentOpenedForms) {
            if (openedForm?.formGroup?.form) {
                const result = await openedForm.formGroup.form.isEdited;
                if (result) {
                    return true;
                }
            }
        }

        return false;
    }

    public async setHomepage(form: IWindowable | IForm | string): Promise<Homepage> {
        const container = new FormGroupContainer();
        this._formsTarget.appendChild(container);

        this._homepage = new Homepage(form, container, this._formsTarget, this._createOpenedFormInfo);

        // Pokud je při inicializaci homepage nějaký formulář už otevřený (např. z odkazu), tak není chtěné homepage zobrazovat
        // Inicializace a zobrazení otevíraného formuláře totiž probíhá před inicializací homepage, takže se homepage zobrazí přes něj,
        // proto se v takové situaci homepage skryje a původně otevíraný formulář se znovu zobrazí
        if (this._openedFormIdsCounters.size > 0) {
            this._homepage.hide(true);
            this.openedForms[0].formGroup.show();
        } else {
            await this._homepage.show();
        }

        return this._homepage;
    }

    public async init(): Promise<void> {
        this._favoriteForms = await this._getFavoriteForms();

        this.formOpened.add((_iformManager, openedFormInfo) => {
            if (openedFormInfo.formGroup.form instanceof IForm) {
                // custom title může být nastavený ještě před tím, než proběhne otevření, v takovém případě vyvoláme událost explicitně
                const customTitle = openedFormInfo.formGroup.form.customTitle;
                if (customTitle) {
                    this.formCustomTitleChanged.trigger(this, { formInstanceId: openedFormInfo.formGroup.formInstanceId, customTitle });
                }
                openedFormInfo.formGroup.form.titleChanged.add((_form, title) => {
                    this.formCustomTitleChanged.trigger(this, { formInstanceId: openedFormInfo.formGroup.formInstanceId, customTitle: title });
                });
            }

            const triggerFormHideCloseButtonChanged = (formInstanceId: string, hideCloseButton: boolean) => {
                this.formHideCloseButtonChanged.trigger(this, { formInstanceId: formInstanceId, hideCloseButton: hideCloseButton });
            };

            const hideCloseButton = openedFormInfo.formGroup.form.getState(WindowState.closeDisabled);
            if (hideCloseButton) {
                triggerFormHideCloseButtonChanged(openedFormInfo.formGroup.formInstanceId, hideCloseButton);
            }

            openedFormInfo.formGroup.form.stateChanged.add((_sender, stateChangedArgs) => {
                switch (stateChangedArgs.state) {
                    case WindowState.closeDisabled:
                        triggerFormHideCloseButtonChanged(openedFormInfo.formGroup.formInstanceId, stateChangedArgs.value);
                        break;
                }
            });
        });

        this._initialized.resolve(this);
    }

    public openForm(formName: string, parametricData?: IFRMParams, preferExistingForm?: boolean): Promise<IOpenedForm> {
        return this._openForm(formName, true, parametricData, preferExistingForm);
    }

    public async openCustomModalForm<TForm extends IWindowable>(form: TForm): Promise<TForm> {
        const isFormGroupContainerVisible = this.lastActiveForm?.formGroup?.container?.visible;
        (isFormGroupContainerVisible) ? await this.lastActiveForm?.formGroup?.openCustomModalForm(form) : await this._homepage?.formGroup?.openCustomModalForm(form);
        return form;
    }

    public openCustomForm<TForm extends IWindowable>(form: TForm, showInMenu: boolean = true): Promise<IOpenedForm> {
        return this._openForm(form, showInMenu, null, false);
    }

    private async _openForm<TForm extends IWindowable>(inputFormData: TForm | string, showInMenu: boolean = true, parametricData?: IFRMParams, preferExistingForm?: boolean): Promise<IOpenedForm> {
        UIManager.instance.showLoader();
        this._openedFormDefer = PromiseUtils.deferStateful<IOpenedForm>();
        const formName = typeof inputFormData === "string" ? inputFormData : `customForm-${DefFormManager._customFormNum++}`;

        try {
            if (preferExistingForm && typeof inputFormData === "string") {
                const openedForm = this._openedForms.find(item => item.openedFormInfo.formCode === inputFormData && item.openedFormInfo.profileId === parametricData?.applyFormProfileId);
                if (openedForm) {
                    this.switchToForm(openedForm.openedFormInfo.formInstanceId);
                    return openedForm;
                }
            }

            this._hideAllForms();
            this._homepage?.hide();

            const container = new FormGroupContainer();
            const formInstanceId = this.createFormInstanceId(formName, parametricData);
            container.formName = formName;

            this._formsTarget.appendChild(container);
            let fgroup: FormGroup = null;

            if (typeof inputFormData === "string") {
                fgroup = await FormGroup.initialize(formInstanceId, { formName, container, params: parametricData });
            } else {
                fgroup = await FormGroup.initialize(formInstanceId, { container, form: inputFormData, setFormUI: true });
            }

            const form = fgroup.form as IForm;

            if (form.isPreviewMode)
                form.customTitle = `${form.formName} - Preview`;

            const saveInHistory = showInMenu && parametricData?.isFromMainMenu && parametricData?.menuItemId != undefined && !this.previewId;

            const meanwhileOpenedForm = this.openedForms.find(f => f.formGroup.container.visible && f.openedFormInfo.formInstanceId !== fgroup.formInstanceId) || this._homepage?.formGroup?.container.visible;

            const openedInfo = this._createOpenedFormInfo(fgroup, formInstanceId, parametricData, !!meanwhileOpenedForm, showInMenu);

            if (meanwhileOpenedForm) {
                form.formUI.formMenu?.setProperty(FormUICustomProperty.formMenuVisibility, false);
            }

            this._addFormToOpenedForms(openedInfo);

            fgroup.formClosed.add((fg, fe) => {
                const formData = this._removeFormFromOpenedForms(fe.form);

                fe.form.setState(WindowState.visible, false);

                if (formData) { // form data muze byt null pokud se jedna o modalni formular
                    if (this.lastOpenedForm)
                        this.switchToForm(this.lastOpenedForm.openedFormInfo.formInstanceId);
                    else
                        this._homepage?.show();

                    this._activityFormList = this._activityFormList.filter((value) => value != formData.openedFormInfo.formInstanceId);

                    if (saveInHistory)
                        this._openedFormsHistoryManager.updateOpenedFormsHistory(fg.form, formData.openedFormInfo.menuItemId);

                    this.formClosed.trigger(this, formData);
                }
            });

            fgroup.form.setState(WindowState.favorite, this._isFavoriteForm(parametricData?.menuItemId, form.formName, form.profileManager?.selectedProfile?.id));

            if (saveInHistory)
                this._openedFormsHistoryManager.updateOpenedFormsHistory(fgroup.form, parametricData.menuItemId);

            this._openedFormDefer.resolve(openedInfo);

            // Zobrazit formulář, jen pokud se nepřeplo během otevírání na jiný
            if (!meanwhileOpenedForm)
                fgroup.show();

            return openedInfo;
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            this._openedFormDefer.reject(e);

            return null;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public openFormModal(options: IFormGroupOpenModalParams): Promise<IWindowable> {
        if (options.parentFormId) {
            const form = this.getOpenedForm(options.parentFormId);
            if (form)
                return form.formGroup.openForm(options);
        }
        const isFormGroupContainerVisible = this.lastActiveForm?.formGroup?.container?.visible;
        return (isFormGroupContainerVisible) ? this.lastActiveForm?.formGroup.openForm(options) : this._homepage?.formGroup?.openForm(options);
    }

    public async isFavoriteForm(menuId: number, formName: string, profileId?: number): Promise<boolean> {
        return this._isFavoriteForm(menuId, formName, profileId);
    }

    public async closeAllForms(): Promise<boolean> {
        let finalResult = false;
        const currentOpenedForms = [...this._openedForms, this._homepage];

        for (const openedForm of currentOpenedForms) {
            if (openedForm?.formGroup?.form?.isEdited) {
                openedForm.formGroup.show();
                const result = await openedForm.formGroup.form.close();
                if (!result) {
                    return false;
                }
                finalResult = finalResult || result;
            }
        }
        return finalResult;
    }

    public async closeForm(openedFormId: string): Promise<boolean> {
        const fg = this._openedFormsMap.get(openedFormId);
        const closeResult = await fg.formGroup.form.close();
        //fg.formGroup.close();

        return closeResult;
    }

    public switchToForm(formInstanceId: string): boolean {
        const openedFormData = this._openedFormsMap.get(formInstanceId);
        if (openedFormData) {
            if (this._activityFormList.indexOf(formInstanceId) !== -1) {
                this._activityFormList.splice(this._activityFormList.indexOf(formInstanceId), 1);
                this._activityFormList.push(formInstanceId);
            }
            this._activateForm(openedFormData);
            this.formSwitched.trigger(this, openedFormData);
            return true;
        }
        return false;
    }

    protected _hideAllForms(keepOpenItem?: IForm): void {
        for (const form of this._openedForms) {
            if (form.formGroup.form == keepOpenItem)
                continue;
            form.formGroup.hide();
        }
    }

    public hideAllForms(): void {
        this._hideAllForms();
        this._homepage?.show();
    }

    protected _activateForm(form: IOpenedForm): void {
        this._hideAllForms();
        this._homepage?.hide();
        form.formGroup.show();
        this._activityFormList.push(form.openedFormInfo.formInstanceId);
    }

    /**
     * Změní status oblíbenosti.
     * @param menuId Id menu
     * @param formName Název formuláře
     * @param value Stav na který se přepne oblíbenost
     * @param profile Profil
     */
    protected async _toggleFavoriteForm(menuId: number, formName: string, value: boolean, profile?: IFormProfile): Promise<void> {
        this._favoriteForms = await this._getFavoriteForms();
        let change = false;
        if (value) {
            if (!this._isFavoriteForm(menuId, formName, profile?.id)) {
                this._favoriteForms.push({ menuId: menuId, formCode: formName, profileId:profile?.id });
                change = true;
            }
        } else {
            const index = this._favoriteForms.findIndex(form => form.menuId === menuId && form.formCode === formName && form.profileId == profile?.id);
            if (index >= 0) {
                this._favoriteForms.splice(index, 1);
                change = true;
            }
        }
        if (change) { // ulozeni se provede pouze pri zmene
            if (await this._saveFavoriteForms(JSON.stringify({ forms: this._favoriteForms })))
                this.favoriteFormSet.trigger(this, { menuId, formCode: formName, isFavorite: value, profile: profile });
        }
    }

    protected async _getFavoriteForms(): Promise<IFavoriteFormItem[]> {
        try {
            UIManager.instance.showLoader();
            const data = await OWManager.runStaticMethodWithParams({
                className: "UserProfile",
                methodName: "LoadFavoriteForms",
                params: ["MW"],
            });
            return !data.resultValue ? [] : (JSON.parse(data.resultValue as string) as IFavoriteFormsProfile).forms;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    protected _loadLocalyEvaluableFormConditions(formName: string): void {
        formName = formName?.toLowerCase();
        const script = document.createElement("script");
        const handlerUrlPart = isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03")
            ? `/mwclient/js/${formName}.aspx`
            : `/api/v01/as/gui/forms/${formName}/`;
        script.src = `${AppContext.instance.addresses.serviceUrl}${handlerUrlPart}?clientType=MultiWeb&getOnlyConditions=true`;
        document.head.insertAdjacentElement("beforeend", script);
    }

    /**
     * Vytvoří formInstanceId pro otvíraný formulář se zadanými parametry. Aktualizuje čítač instancí.
     * @param formCode Kód formuláře.
     * @param formParameters Parametry pro otevření.
     */
    public createFormInstanceId(formCode: string, formParameters?: IFRMParams): string {
        const formProfileId = formParameters ? Number(formParameters.applyFormProfileId) || undefined : undefined;
        const menuItemId = formParameters ? formParameters.menuItemId : undefined;
        const counterKey = this._createOpenedFormCounterKey(formCode, menuItemId, formProfileId);
        const counterValue = this._openedFormIdsCounters.get(counterKey) || 0;
        const formInstanceNumber = counterValue + 1;
        this._openedFormIdsCounters.set(counterKey, formInstanceNumber);
        return `fc:${formCode}-mid:${menuItemId ?? ""}-fp:${formProfileId ?? ""}-fid:${formInstanceNumber}`; // `
    }

    /**
     * Vytváří informace o otevřeném formuláři
     * @param formGroup
     * @param formInstanceId
     * @param formParameters
     */
    protected _createOpenedFormInfo(formGroup: IFormGroup, formInstanceId: string, formParameters?: IFRMParams, openInBackground: boolean = false, showInMenu: boolean = true): IOpenedForm | null {
        const formProfileId = formParameters ? Number(formParameters.applyFormProfileId) || undefined : undefined;
        const menuItemId = formParameters ? formParameters.menuItemId : undefined;
        let selectedId = formParameters?.selectedID;
        if (selectedId == null) {
            selectedId = formParameters?.detailFormObject?.id;
        }

        return {
            formGroup: formGroup,
            openedFormInfo: {
                formInstanceId,
                profileId: formProfileId,
                menuItemId: menuItemId,
                titleLocKey: formGroup.form.titleLocKey,
                formCode: formGroup.formName,
                selectedId,
                visibleOnlyDetail: formParameters?.visibleOnlyDetail,
                showInMenu,
                isEdited: formGroup.form.isEdited,
            },
            openInBackground,
        } as IOpenedForm;
    }

    protected _addFormToOpenedForms(newOpenedFormInfo: IOpenedForm): void {
        this._addFormToStructures(newOpenedFormInfo);
        this.formOpened.trigger(this, newOpenedFormInfo);
    }

    protected _addFormToStructures(newOpenedFormInfo: IOpenedForm): void {
        this._openedForms.push(newOpenedFormInfo);
        newOpenedFormInfo.formGroup.container.setAttribute("data-qa", newOpenedFormInfo.openedFormInfo.formInstanceId);

        this._openedFormsMap.set(newOpenedFormInfo.openedFormInfo.formInstanceId, newOpenedFormInfo);
        this._activityFormList.push(newOpenedFormInfo.openedFormInfo.formInstanceId);
    }

    /**
     * Odstraní formulář z kolekce otevřených formulářů
     * @param form Formulář, který se má zavřít
     * @returns Vrací data popisující odstraněný formulář; pokud nebyl formulář v kolekci nalezen, vrací null
     */
    protected _removeFormFromOpenedForms(form: IWindowable): IOpenedForm | null {
        const index = this._openedForms.findIndex((item) => item.formGroup.form == form);

        if (index < 0)
            return null;

        this._openedForms.splice(index, 1);

        const openedFormData = this._findOpenedFormData(form);
        if (openedFormData) {
            const openedFormDeferInstanceId = this._openedFormDefer?.promiseValue?.openedFormInfo.formInstanceId;
            const removedFormInstanceId = openedFormData?.openedFormInfo.formInstanceId;

            this._openedFormsMap.delete(removedFormInstanceId);

            if (openedFormDeferInstanceId === removedFormInstanceId && openedFormDeferInstanceId !== undefined) { // vyčištění promise po zavření formuláře, aby tam nezůstával viset v paměti
                this._openedFormDefer = null;
            }

            return openedFormData;
        }

        return null;
    }

    private _findOpenedFormData(form: IWindowable): IOpenedForm | null {
        for (const [, openedFormItem] of this._openedFormsMap) {
            if (openedFormItem.formGroup.form === form)
                return openedFormItem;
        }

        return null;
    }

    /**
     * Vytvoří klíč pro počítadlo instancí otevřených položek menu. Generuje odlišné klíče pro menu položku formuláře
     * a menu položku profilu, i když patří oba ke stejnému formuláři.
     * @param formCode Kód formuláře. Slouží jako záloha pro případ, že byl otevřen formulář, který nemá položku v menu.
     * @param menuItemId Id položky menu.
     * @param formProfileId Id profilu, který byl aplikován na otevřený formulář.
     */
    protected _createOpenedFormCounterKey(formCode: string, menuItemId?: number, formProfileId?: number): string {
        if (formProfileId && menuItemId) {
            return `mid:${menuItemId ?? ""}-fp:${formProfileId ?? ""}`;
        } else if (menuItemId) {
            return menuItemId.toString();
        } else {
            return formCode;
        }
    }

    private async _saveFavoriteForms(data: string): Promise<boolean> {
        return (await OWManager.runStaticMethodWithParams({
            className: "UserProfile",
            methodName: "SaveFavoriteForms",
            params: [data, "MW"],
        })
        ).resultValue;
    }

    private _isFavoriteForm(menuId: number, formName: string, profileId?: number): boolean {
        return this._favoriteForms.some(form => form.menuId === menuId && form.formCode === formName && form.profileId == profileId);
    }
    // #endregion
}
